import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Edit2, Send, ArrowLeft, Users, CheckCircle2, XCircle, SkipForward } from "lucide-react";
import { getCampaign, sendCampaignNow } from "@/lib/admin-api";
import { toast } from "sonner";
import { formatMtyDateTimeShort as fmtMTY } from "@/lib/mty-date";

export default function AdminCampaignDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await getCampaign(id);
      setData(r);
    } catch {
      toast.error("Error cargando campaña");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const doSendNow = async () => {
    if (!window.confirm("¿Enviar AHORA a toda la audiencia?")) return;
    try {
      await sendCampaignNow(id);
      toast.success("Envío iniciado");
      setTimeout(load, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error iniciando envío");
    }
  };

  if (loading || !data) return <div className="p-8 text-neutral-500">Cargando...</div>;
  const { campaign, sends } = data;
  const stats = campaign.stats || { recipients: 0, sent: 0, failed: 0, skipped: 0 };

  return (
    <div className="p-5 md:p-8 max-w-6xl">
      <button onClick={() => nav("/admin/campaigns")} className="text-sm text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Volver
      </button>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="section-title text-3xl md:text-4xl font-black">{campaign.name}</h1>
          <p className="text-neutral-500 text-sm mt-1">Asunto: {campaign.subject_a}{campaign.ab_enabled && campaign.subject_b ? ` / ${campaign.subject_b}` : ""}</p>
          <p className="text-xs text-neutral-400 mt-1">Creada {fmtMTY(campaign.created_at)} · Estado: <strong>{campaign.status}</strong></p>
        </div>
        <div className="flex gap-2">
          {campaign.status !== "sent" && (
            <button onClick={() => nav(`/admin/campaigns/${id}/edit`)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm border border-neutral-300 hover:bg-neutral-50" data-testid="edit-btn">
              <Edit2 className="w-4 h-4" /> Editar
            </button>
          )}
          {campaign.status !== "sent" && campaign.status !== "sending" && (
            <button onClick={doSendNow} className="bg-green-600 text-white hover:bg-green-700 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold" data-testid="send-now-btn">
              <Send className="w-4 h-4" /> Enviar ahora
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="Audiencia" value={stats.recipients} icon={Users} />
        <Kpi label="Entregados" value={stats.sent} color="text-green-600" icon={CheckCircle2} />
        <Kpi label="Fallidos" value={stats.failed} color="text-red-600" icon={XCircle} />
        <Kpi label="Omitidos" value={stats.skipped} color="text-neutral-500" icon={SkipForward} />
      </div>

      {campaign.ab_enabled && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
          <p className="font-semibold text-orange-900 text-sm">Resultados A/B</p>
          <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
            <div><strong>Variante A:</strong> {sends.filter(s => s.variant === "a" && s.status === "sent").length} enviados</div>
            <div><strong>Variante B:</strong> {sends.filter(s => s.variant === "b" && s.status === "sent").length} enviados</div>
          </div>
        </div>
      )}

      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
          <h3 className="font-black section-title">Registro de envíos ({sends.length})</h3>
        </div>
        {sends.length === 0 ? (
          <div className="p-8 text-center text-neutral-500">Todavía no se ha enviado a nadie.</div>
        ) : (
          <div className="max-h-[500px] overflow-auto">
            <table className="w-full text-sm" data-testid="sends-table">
              <thead className="bg-neutral-50 text-xs uppercase text-neutral-600 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2.5">Fecha</th>
                  <th className="text-left px-4 py-2.5">Email</th>
                  <th className="text-left px-4 py-2.5">Variante</th>
                  <th className="text-left px-4 py-2.5">Asunto</th>
                  <th className="text-left px-4 py-2.5">Estado</th>
                  <th className="text-left px-4 py-2.5">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {sends.map((s) => (
                  <tr key={s.id} className="border-t border-neutral-100">
                    <td className="px-4 py-2.5 text-xs text-neutral-500 whitespace-nowrap">{fmtMTY(s.sent_at)}</td>
                    <td className="px-4 py-2.5 font-medium">{s.email}</td>
                    <td className="px-4 py-2.5"><span className="text-xs px-2 py-0.5 bg-neutral-100 rounded">{s.variant?.toUpperCase() || "A"}</span></td>
                    <td className="px-4 py-2.5 max-w-[280px] truncate text-neutral-600">{s.subject}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                        s.status === "sent" ? "bg-green-100 text-green-800"
                        : s.status === "failed" ? "bg-red-100 text-red-800"
                        : "bg-neutral-200 text-neutral-600"
                      }`}>{s.status}</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-neutral-500 max-w-[240px] truncate">{s.error || s.resend_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color = "text-neutral-900" }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4">
      <div className="flex items-center gap-2 text-xs text-neutral-500 uppercase tracking-wider">
        {Icon && <Icon className="w-3.5 h-3.5" />}{label}
      </div>
      <div className={`mt-1 text-3xl font-black ${color}`}>{value}</div>
    </div>
  );
}
